import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import VarietySelector from './VarietySelector';
import CustomerSelector from './CustomerSelector';

const { FiEdit2, FiTrash2, FiPower, FiX, FiPlusCircle, FiMinusCircle, FiPlus, FiArrowUp, FiArrowDown } = FiIcons;

const RecurringRules = () => {
    const [rules, setRules] = useState([]);
    const [varieties, setVarieties] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [formData, setFormData] = useState({
        customer: '',
        intervalDays: 7,
        baseDate: format(new Date(), 'yyyy-MM-dd'),
        endDate: '',
        notes: '',
        priceType: 'Standard',
        deliveryFee: '',
        items: []
    });

    useEffect(() => {
        fetchRules();
        fetchVarieties();
        fetchCustomers();
    }, []);

    const fetchRules = async () => {
        try {
            const res = await fetch('http://localhost:3011/api/recurring-rules');
            const data = await res.json();
            setRules(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchVarieties = async () => {
        try {
            const res = await fetch('/api/varieties');
            if (res.ok) setVarieties(await res.json());
        } catch (error) {
            console.error(error);
        }
    };

    const fetchCustomers = async () => {
        try {
            const res = await fetch('/api/customers');
            if (res.ok) setCustomers(await res.json());
        } catch (error) {
            console.error(error);
        }
    };

    const handleToggleActive = async (rule) => {
        try {
            const response = await fetch(`/api/recurring-rules/${rule.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ active: !rule.active })
            });
            if (response.ok) fetchRules();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this recurring rule?')) return;
        try {
            const response = await fetch(`/api/recurring-rules/${id}`, {
                method: 'DELETE'
            });
            if (response.ok) fetchRules();
        } catch (error) {
            console.error(error);
        }
    };

    const handleNewRule = () => {
        setEditingRule(null);
        setFormData({
            customer: '',
            intervalDays: 7,
            baseDate: format(new Date(), 'yyyy-MM-dd'),
            endDate: '',
            notes: '',
            priceType: 'Standard',
            deliveryFee: '',
            items: [{ varietyId: "", quantity: "", bufferQuantity: "" }]
        });
        setIsModalOpen(true);
    };

    const handleEdit = (rule) => {
        setEditingRule(rule);
        setFormData({
            customer: rule.customer,
            intervalDays: rule.intervalDays,
            baseDate: format(new Date(rule.baseDate), 'yyyy-MM-dd'),
            endDate: rule.endDate ? format(new Date(rule.endDate), 'yyyy-MM-dd') : '',
            notes: rule.notes || '',
            priceType: rule.priceType || 'Standard',
            deliveryFee: rule.deliveryFee || '',
            items: rule.items.map(item => ({
                varietyId: item.varietyId,
                quantity: item.quantity,
                bufferQuantity: item.bufferQuantity || '' // Ensure not null
            }))
        });
        setIsModalOpen(true);
    };

    const handleAddItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { varietyId: "", quantity: "", bufferQuantity: "" }]
        }));
    };

    const handleRemoveItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const handleMoveItem = (index, direction) => {
        const newItems = [...formData.items];
        if (direction === "up" && index > 0) {
            [newItems[index], newItems[index - 1]] = [
                newItems[index - 1],
                newItems[index],
            ];
        } else if (direction === "down" && index < newItems.length - 1) {
            [newItems[index], newItems[index + 1]] = [
                newItems[index + 1],
                newItems[index],
            ];
        }
        setFormData((prev) => ({ ...prev, items: newItems }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let url = '/api/recurring-rules';
            let method = 'POST';
            let body = { ...formData };

            if (editingRule) {
                url = `/api/recurring-rules/${editingRule.id}`;
                method = 'PUT';
                // Preserve active state or default to true if undefined
                body.active = (editingRule.active !== undefined) ? editingRule.active : true;
            } else {
                body.isSudden = false;
                body.dateType = 'delivery'; // Default
            }

            const response = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                setIsModalOpen(false);
                fetchRules();
                if (!editingRule) alert("Recurring order created successfully!");
            } else {
                const errData = await response.json().catch(() => ({}));
                console.error("Failed to save rule", errData);
                alert(`Failed to save. ${errData.error || 'Please check all fields.'}`);
            }
        } catch (error) {
            console.error(error);
            alert("Network error. Please try again.");
        }
    };

    return (
        <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] tracking-tight">Recurring Orders</h1>
                    <p className="text-gray-400 mt-2 font-light">Manage automated recurring orders</p>
                </div>
                <button
                    onClick={handleNewRule}
                    className="bg-[#16a34a] hover:bg-green-700 text-white px-6 py-2.5 rounded-xl flex items-center space-x-2 transition-all shadow-md shadow-green-100 font-medium"
                >
                    <SafeIcon icon={FiPlus} />
                    <span>New Order</span>
                </button>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-100">
                            <tr>
                                <th className="px-6 py-4">ID</th>
                                <th className="px-6 py-4">Customer</th>
                                <th className="px-6 py-4">Schedule</th>
                                <th className="px-6 py-4">Total</th>
                                <th className="px-6 py-4">Items</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {rules.map(rule => (
                                <tr key={rule.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-6 py-4 text-gray-400">#{rule.id}</td>
                                    <td className="px-6 py-4 font-medium text-gray-900">{rule.customer}</td>
                                    <td className="px-6 py-4 text-gray-600">
                                        <div className="flex flex-col">
                                            <span>Starts: {format(new Date(rule.baseDate), 'MMM dd, yyyy')}</span>
                                            <span className="text-xs text-gray-400">Every {rule.intervalDays} days</span>
                                            {rule.endDate && <span className="text-xs text-red-400">Ends: {format(new Date(rule.endDate), 'MMM dd, yyyy')}</span>}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 font-medium text-[#1e3a8a]">
                                        {(() => {
                                            const itemsTotal = (rule.items || []).reduce((sum, item) => {
                                                const price = rule.priceType === 'Supplier' ? item.supplierPrice : item.price;
                                                return sum + (parseFloat(price || 0) * item.quantity);
                                            }, 0);
                                            const fee = parseFloat(rule.deliveryFee || 0);
                                            return `RM ${(itemsTotal + fee).toFixed(2)}`;
                                        })()}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col space-y-1">
                                            {rule.items && rule.items.map((item, idx) => (
                                                <span key={idx} className="text-gray-600">
                                                    {item.quantity} x {item.varietyName}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${rule.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                            {rule.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => handleEdit(rule)}
                                                className="p-2 text-[#1e3a8a] hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Edit Rule"
                                            >
                                                <SafeIcon icon={FiEdit2} />
                                            </button>
                                            <button
                                                onClick={() => handleToggleActive(rule)}
                                                className={`p-2 rounded-lg transition-colors ${rule.active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                                                title={rule.active ? "Deactivate" : "Activate"}
                                            >
                                                <SafeIcon icon={FiPower} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(rule.id)}
                                                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete Rule"
                                            >
                                                <SafeIcon icon={FiTrash2} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {rules.length === 0 && !loading && (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                                        No recurring orders found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit/Create Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 md:bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in">
                        <div className="p-6 md:p-8">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h2 className="text-2xl font-light text-[#1e3a8a]">{editingRule ? 'Edit Recurring Order' : 'New Recurring Order'}</h2>
                                    <p className="text-sm text-gray-400 font-light mt-1">
                                        {editingRule ? `Update schedule and items for ${editingRule.customer}` : 'Create a new automated recurring order'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-all"
                                >
                                    <SafeIcon icon={FiX} className="text-xl" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">

                                {/* Customer Selection (Only for New Rules) */}
                                {!editingRule && (
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Customer</label>
                                        <CustomerSelector
                                            customers={customers}
                                            value={formData.customer}
                                            onChange={(val) => setFormData({ ...formData, customer: val })}
                                        />
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Base Date (First Order)</label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.baseDate}
                                            onChange={(e) => setFormData({ ...formData, baseDate: e.target.value })}
                                            className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#16a34a] focus:ring-4 focus:ring-[#16a34a]/10 outline-none transition-all font-light"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Interval (Days)</label>
                                        <input
                                            type="number"
                                            min="1"
                                            required
                                            value={formData.intervalDays}
                                            onChange={(e) => setFormData({ ...formData, intervalDays: parseInt(e.target.value) })}
                                            className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#16a34a] focus:ring-4 focus:ring-[#16a34a]/10 outline-none transition-all font-light"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">End Date (Optional)</label>
                                        <input
                                            type="date"
                                            value={formData.endDate}
                                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                            className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#16a34a] focus:ring-4 focus:ring-[#16a34a]/10 outline-none transition-all font-light"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Price Type</label>
                                        <select
                                            value={formData.priceType}
                                            onChange={(e) => setFormData({ ...formData, priceType: e.target.value })}
                                            className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#16a34a] focus:ring-4 focus:ring-[#16a34a]/10 outline-none transition-all font-light"
                                        >
                                            <option value="Standard">Standard</option>
                                            <option value="Supplier">Supplier</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Delivery Fee</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">RM</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={formData.deliveryFee}
                                                onChange={(e) => setFormData({ ...formData, deliveryFee: e.target.value })}
                                                className="w-full px-4 pl-10 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#16a34a] focus:ring-4 focus:ring-[#16a34a]/10 outline-none transition-all font-light"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Items Section */}
                                <div className="space-y-4">
                                    <div className="p-3 rounded-lg flex justify-between items-center bg-[#16a34a]/10">
                                        <h3 className="text-sm font-medium uppercase tracking-widest text-[#1e3a8a]">
                                            Production Items
                                        </h3>
                                        <button
                                            type="button"
                                            onClick={handleAddItem}
                                            className="text-[#16a34a] hover:text-green-700 font-medium text-xs flex items-center space-x-1"
                                        >
                                            <SafeIcon icon={FiPlusCircle} />
                                            <span>Add Item</span>
                                        </button>
                                    </div>

                                    <div className="space-y-3">
                                        {formData.items.map((item, index) => (
                                            <div key={index} className="grid grid-cols-12 gap-3 items-start bg-gray-50 p-4 rounded-xl border border-gray-100">
                                                <div className="col-span-12 md:col-span-6">
                                                    <label className="block text-[10px] font-medium text-gray-400 uppercase tracking-widest mb-1">
                                                        Variety
                                                    </label>
                                                    <VarietySelector
                                                        value={item.varietyId}
                                                        onChange={(val) => handleItemChange(index, "varietyId", val)}
                                                        varieties={varieties}
                                                        placeholder="Select Variety"
                                                    />
                                                </div>
                                                <div className="col-span-5 md:col-span-2">
                                                    <label className="block text-[10px] font-medium text-gray-400 uppercase tracking-widest mb-1">
                                                        Order Qty {varieties.find(v => v.id == item.varietyId)?.category === 'Edible Flowers' ? '(g)' : ''}
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={item.quantity}
                                                        onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none bg-white text-sm font-medium text-center focus:border-[#16a34a]"
                                                        min="0"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-span-5 md:col-span-2">
                                                    <label className="block text-[10px] font-medium text-orange-400 uppercase tracking-widest mb-1">
                                                        Buffer Qty
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={item.bufferQuantity}
                                                        onChange={(e) => handleItemChange(index, "bufferQuantity", e.target.value)}
                                                        className="w-full px-3 py-2 border border-orange-100 rounded-lg outline-none bg-white text-sm font-medium text-center text-orange-600 focus:border-orange-500"
                                                        min="0"
                                                    />
                                                </div>
                                                <div className="col-span-2 md:col-span-1 flex flex-col justify-center items-center space-y-1 pt-4">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveItem(index, "up")}
                                                        disabled={index === 0}
                                                        className={`p-1 rounded hover:bg-gray-100 ${index === 0 ? "text-gray-300" : "text-gray-500 hover:text-blue-600"}`}
                                                    >
                                                        <SafeIcon icon={FiArrowUp} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveItem(index)}
                                                        className="text-red-400 hover:text-red-600 transition-colors p-1"
                                                    >
                                                        <SafeIcon icon={FiMinusCircle} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleMoveItem(index, "down")}
                                                        disabled={index === formData.items.length - 1}
                                                        className={`p-1 rounded hover:bg-gray-100 ${index === formData.items.length - 1 ? "text-gray-300" : "text-gray-500 hover:text-blue-600"}`}
                                                    >
                                                        <SafeIcon icon={FiArrowDown} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Estimated Total */}
                                <div className="flex justify-between items-center py-4 border-t border-dashed border-gray-200">
                                    <span className="text-sm font-medium text-gray-500 uppercase tracking-widest">
                                        Estimated Total per Delivery
                                    </span>
                                    <span className="text-xl font-light text-[#1e3a8a]">
                                        RM {(() => {
                                            const itemsTotal = formData.items.reduce((sum, item) => {
                                                const v = varieties.find(v => v.id.toString() === item.varietyId.toString());
                                                const price = v ? parseFloat((formData.priceType === 'Supplier' ? v.supplierPrice : v.price) || 0) : 0;
                                                return sum + (price * parseInt(item.quantity || 0));
                                            }, 0);
                                            const fee = parseFloat(formData.deliveryFee || 0);
                                            return (itemsTotal + fee).toFixed(2);
                                        })()}
                                    </span>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Notes</label>
                                    <textarea
                                        rows="2"
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-[#16a34a] focus:ring-4 focus:ring-[#16a34a]/10 outline-none transition-all font-light resize-none"
                                        placeholder="Add any special instructions..."
                                    />
                                </div>

                                <div className="flex justify-end pt-4 space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="px-6 py-2.5 text-gray-500 font-medium hover:bg-gray-50 rounded-xl transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-8 py-2.5 bg-[#1e3a8a] hover:bg-blue-900 text-white rounded-xl shadow-lg shadow-blue-200 font-medium transition-all transform active:scale-95"
                                    >
                                        Save Order
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecurringRules;
